import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { LoadErrorState, NoMatchesState } from '@/components/ListStates';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Pagination } from '@/components/Pagination';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Modal } from '@/components/Modal';
import { documentsService, CrmDocument, DocumentType, DocumentStatus, CashflowSnapshot, CashflowBucketTotal } from '@/services/documents.service';
import { Plus, FileText, Search, Trash2, BookOpen, Sparkles, CheckCircle2, AlertCircle, Clock } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { useCompanyCurrency } from '@/contexts/WorkspaceContext';

const TYPE_LABELS: Record<DocumentType, string> = {
  quotation: 'Quotation', invoice: 'Invoice', proposal: 'Proposal',
  note: 'Note', contract: 'Contract', other: 'Other',
};

const STATUS_COLORS: Record<DocumentStatus, string> = {
  draft:    'bg-surface-alt text-text-muted',
  sent:     'bg-info-soft text-info',
  viewed:   'bg-primary-soft text-primary',
  accepted: 'bg-success-soft text-success',
  declined: 'bg-danger-soft text-danger',
  expired:  'bg-warning-soft text-warning',
};

export default function DocumentsList() {
  const companyCurrency = useCompanyCurrency();
  const [data, setData] = useState<{ items: CrmDocument[]; total: number; page: number; limit: number } | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState<DocumentType | ''>('');
  const [page, setPage] = useState(1);
  const [del, setDel] = useState<CrmDocument | null>(null);
  const [creating, setCreating] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [templates, setTemplates] = useState<CrmDocument[] | null>(null);
  const [cloning, setCloning] = useState<string | null>(null);
  const [cashflow, setCashflow] = useState<CashflowSnapshot | null>(null);
  const nav = useNavigate();

  // Pull cashflow once on mount — small aggregation, refreshes when filters change
  // wouldn't tell the user anything new (cashflow is whole-tenant scoped).
  useEffect(() => {
    documentsService.cashflow().then(setCashflow).catch(() => undefined);
  }, []);

  const refresh = () => {
    setLoadError(false);
    return documentsService.list({
      q: q || undefined,
      type: typeFilter || undefined,
      page,
      limit: 25,
    }).then(setData).catch(() => setLoadError(true));
  };
  const filtersActive = !!q.trim() || !!typeFilter;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [q, typeFilter, page]);

  const createNew = async () => {
    setCreating(true);
    try {
      const created = await documentsService.create({
        type: 'quotation',
        title: 'Untitled Quotation',
        body: '',
      });
      nav(`/documents/${created.documentUid}`);
    } catch (e: any) {
      // axios interceptor already toasted backend message
    } finally { setCreating(false); }
  };

  const onDelete = async () => {
    if (!del) return;
    try {
      await documentsService.remove(del.documentUid);
      toast.success('Document removed');
      setDel(null);
      refresh();
    } catch { /* interceptor */ }
  };

  const openPicker = async () => {
    setPickerOpen(true);
    if (!templates) {
      try {
        const r = await documentsService.listTemplates();
        setTemplates(r.items);
      } catch {
        setTemplates([]);
      }
    }
  };

  const applyTemplate = async (tpl: CrmDocument) => {
    setCloning(tpl.documentUid);
    try {
      const created = await documentsService.cloneFromTemplate(tpl.documentUid);
      toast.success(`Created "${created.title}" from template`);
      setPickerOpen(false);
      nav(`/documents/${created.documentUid}`);
    } catch { /* interceptor */ }
    finally { setCloning(null); }
  };

  return (
    <>
      <Topbar
        title="Documents"
        breadcrumb="Sales"
        description="Quotations, invoices, proposals, and contracts."
        right={
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={openPicker} iconLeft={<BookOpen size={14} />}>
              From template
            </Button>
            <Button variant="primary" onClick={createNew} loading={creating} iconLeft={<Plus size={14} />}>
              New document
            </Button>
          </div>
        }
      />

      <div className="w-full px-7 py-7 space-y-4">
        {/* Cashflow strip — accounting-lite. Sums per currency across all
            invoices on the tenant. Nothing rendered when there are no invoices. */}
        {cashflow && (cashflow.paid.length > 0 || cashflow.outstanding.length > 0) && (
          <CashflowStrip cf={cashflow} fallbackCurrency={companyCurrency} />
        )}

        <Card padded={false} className="p-3">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              placeholder="Search by title…"
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              iconLeft={<Search size={14} />}
              className="w-72"
            />
            <Select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value as DocumentType | ''); setPage(1); }}>
              <option value="">All types</option>
              {(Object.keys(TYPE_LABELS) as DocumentType[]).map((t) => (
                <option key={t} value={t}>{TYPE_LABELS[t]}</option>
              ))}
            </Select>
          </div>
        </Card>

        {loadError && (
          <LoadErrorState what="your documents" onRetry={() => void refresh()} />
        )}

        {!data && !loadError && <Card><div className="skeleton h-24 rounded-lg" /></Card>}

        {!loadError && data && data.items.length === 0 && filtersActive && (
          <NoMatchesState onClear={() => { setQ(''); setTypeFilter(''); setPage(1); }} />
        )}

        {!loadError && data && data.items.length === 0 && !filtersActive && (
          <EmptyState
            icon={<FileText size={48} className="text-text-muted" />}
            title="No documents yet"
            description="Create your first quotation, invoice, or proposal — with AI assist if you want a fast draft."
            action={<Button onClick={createNew} loading={creating}><Plus size={16} /> New document</Button>}
          />
        )}

        {!loadError && data && data.items.length > 0 && (
          <Card padded={false}>
            <div className="divide-y divide-border">
              {data.items.map((d) => (
                <div key={d.documentUid} className="flex items-center gap-3 px-5 py-3 hover:bg-surface-alt">
                  <div
                    className="flex flex-1 cursor-pointer items-center gap-3 min-w-0"
                    onClick={() => nav(`/documents/${d.documentUid}`)}
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
                      <FileText size={16} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium text-text-primary">{d.title}</span>
                        <span className={clsx('rounded-full px-2 py-0.5 text-2xs font-semibold capitalize', STATUS_COLORS[d.status])}>
                          {d.status}
                        </span>
                        {/* Invoice payment state — only render for invoices. */}
                        {d.type === 'invoice' && d.paidAt && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-success-soft px-2 py-0.5 text-2xs font-semibold text-success">
                            <CheckCircle2 size={10} /> Paid
                          </span>
                        )}
                        {d.type === 'invoice' && !d.paidAt && d.dueDate && new Date(d.dueDate).getTime() < Date.now() && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-danger-soft px-2 py-0.5 text-2xs font-semibold text-danger">
                            <AlertCircle size={10} /> Overdue
                          </span>
                        )}
                        {d.type === 'invoice' && !d.paidAt && (!d.dueDate || new Date(d.dueDate).getTime() >= Date.now()) && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-warning-soft px-2 py-0.5 text-2xs font-semibold text-warning">
                            <Clock size={10} /> Outstanding
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 flex items-center gap-2 text-xs text-text-muted">
                        <span>{TYPE_LABELS[d.type]}</span>
                        {d.amount != null && <span>· {d.amount.toLocaleString()} {d.currency ?? ''}</span>}
                        {d.dueDate && d.type === 'invoice' && !d.paidAt && (
                          <span>· due {new Date(d.dueDate).toLocaleDateString()}</span>
                        )}
                        <span>· {new Date(d.updatedAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setDel(d)}
                    className="rounded-lg p-1.5 text-text-muted hover:bg-danger-soft hover:text-danger"
                    title="Remove"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          </Card>
        )}

        {!loadError && data && data.total > data.limit && (
          <Pagination
            page={page}
            total={data.total}
            limit={data.limit}
            onChange={setPage}
          />
        )}
      </div>

      <ConfirmDialog
        open={!!del}
        title="Remove document?"
        message={del ? `"${del.title}" will be removed.` : ''}
        danger
        onConfirm={onDelete}
        onCancel={() => setDel(null)}
      />

      <Modal open={pickerOpen} onClose={() => setPickerOpen(false)} title="Pick a template" widthClass="max-w-2xl">
        <div className="px-6 py-5">
          <p className="mb-4 text-xs text-text-muted">
            Start a draft from a reusable template. Sample templates are seeded automatically — you can mark
            any of your own documents as a template from the editor too.
          </p>
          {!templates ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => <div key={i} className="skeleton h-16 rounded-lg" />)}
            </div>
          ) : templates.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border bg-surface-alt/40 px-4 py-6 text-center">
              <Sparkles size={20} className="mx-auto mb-2 text-text-muted" />
              <p className="text-xs text-text-muted">
                No templates yet. Create a document, then click <strong>"Save as template"</strong> in the editor.
              </p>
            </div>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2 max-h-96 overflow-y-auto">
              {templates.map((t) => (
                <button
                  key={t.documentUid}
                  type="button"
                  disabled={cloning === t.documentUid}
                  onClick={() => applyTemplate(t)}
                  className="rounded-xl border border-border bg-surface px-4 py-3 text-left transition-colors hover:border-primary hover:bg-primary-soft disabled:opacity-60"
                >
                  <div className="flex items-center gap-2">
                    <FileText size={14} className="text-text-muted" />
                    <span className="text-sm font-semibold text-text-primary">{t.title}</span>
                  </div>
                  <div className="mt-1 text-2xs uppercase tracking-wider text-text-muted">
                    {TYPE_LABELS[t.type] ?? t.type}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}

// ─── Cashflow strip ──────────────────────────────────────────────────────────

type Row = { _id: string; total: number; count: number };

/**
 * One figure per bucket in the company's currency (converted server-side at
 * today's rate); the per-currency amounts sit underneath so nothing is hidden.
 */
function bucket(rows: Row[], total: CashflowBucketTotal | undefined, currency: string) {
  if (total) return { total: total.total, count: total.count, currency, rows, unconverted: total.unconverted };
  // Older API: only safe to add up when there's a single currency.
  const single = rows.length <= 1;
  return {
    total: single ? (rows[0]?.total ?? 0) : null,
    count: rows.reduce((n, r) => n + r.count, 0),
    currency: rows[0]?._id || currency,
    rows,
    unconverted: [] as string[],
  };
}

function CashflowStrip({ cf, fallbackCurrency }: { cf: CashflowSnapshot; fallbackCurrency: string }) {
  const currency = cf.currency ?? fallbackCurrency;
  const paid = bucket(cf.paid, cf.totals?.paid, currency);
  const out = bucket(cf.outstanding, cf.totals?.outstanding, currency);
  const od = bucket(cf.overdue, cf.totals?.overdue, currency);
  const mixed = [cf.paid, cf.outstanding, cf.overdue].some((rows) => rows.some((r) => (r._id || currency) !== currency));
  return (
    <Card padded={false} className="p-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <CashTile tone="text-success" icon={<CheckCircle2 size={14} />} label="Paid" b={paid} />
        <CashTile tone="text-warning" icon={<Clock size={14} />} label="Outstanding" b={out} />
        <CashTile tone="text-danger" icon={<AlertCircle size={14} />} label="Overdue" b={od} />
      </div>
      {mixed && (
        <p className="mt-3 border-t border-border pt-2.5 text-2xs text-text-muted">
          Totals in {currency}, converted from other currencies at {cf.ratesAsOf ? `rates of ${new Date(cf.ratesAsOf).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}` : 'today’s rates'}. Invoices keep their own currency.
        </p>
      )}
    </Card>
  );
}

function CashTile({ tone, icon, label, b }: {
  tone: string; icon: React.ReactNode; label: string; b: ReturnType<typeof bucket>;
}) {
  const fmt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  const foreign = b.rows.filter((r) => (r._id || b.currency) !== b.currency);
  return (
    <div className="flex items-start gap-3">
      <span className={clsx('flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-surface-alt', tone)}>{icon}</span>
      <div className="min-w-0">
        <div className="text-2xs uppercase tracking-wider text-text-muted">{label}</div>
        <div className={clsx('text-base font-semibold tabular-nums', tone)}>
          {b.total === null ? 'Mixed currencies' : `${b.currency} ${fmt(b.total)}`}
        </div>
        <div className="text-2xs text-text-muted">{b.count} invoice{b.count === 1 ? '' : 's'}</div>
        {foreign.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {b.rows.map((r) => (
              <span key={r._id || 'none'} className="rounded bg-surface-alt px-1.5 py-0.5 text-[10px] tabular-nums text-text-secondary">
                {r._id || b.currency} {fmt(r.total)}
              </span>
            ))}
          </div>
        )}
        {b.unconverted.length > 0 && (
          <div className="mt-1 text-[10px] text-warning">No rate for {b.unconverted.join(', ')} — left out of the total</div>
        )}
      </div>
    </div>
  );
}
